import { api } from "@/shared/api";
import type { Compliance } from "@/entities/compliance";

// local-only: B (backend schema gap) — /platform/system/permissions/summary 返; see proposals/2026-05-17-fe-generated-coverage-platform.md
export interface PermissionSummary {
  roleCount: number;
  enabledPermissionGroups: number;
  activeAccounts: number;
}

// local-only: B (backend schema gap) — /platform/system/roles 返
export interface RoleInfo {
  /** 角色 enum 名:PLATFORM_ADMIN / TENANT_ADMIN / TENANT_MANAGER / TENANT_STAFF。 */
  id: string;
  name: string;
  level: number;
  accounts: number;
  desc: string;
  modules: string[];
  risks: string[];
}

// local-only: B (backend schema gap) — 矩阵元胞结构,permissions/matrix 返(只读:后端无 PUT,0926 删掉前端死写口)
export interface ModulePerms {
  page: string;
  view: string;
  edit: string;
  publish: string;
  audit: string;
  scope: string;
}

// local-only: B (backend schema gap) — /platform/system/permissions/matrix 返
export interface PermissionMatrixRow {
  moduleKey: string;
  moduleName: string;
  perms: Record<string, ModulePerms>;
}

/** 当前登录用户的有效权限,sidebar / 按钮可见性的数据源。 */
// local-only: C (drift) — generated MyPermissions.modules: string[] vs handwritten Record<string, ModulePerms>;
// 需要 V24 联调确认 backend 真实返哪种 shape,然后 reconcile yaml or frontend
export interface MyPermissions {
  /** 角色 enum,如 PLATFORM_ADMIN / TENANT_ADMIN / TENANT_MANAGER / TENANT_STAFF。 */
  role: string;
  /** 同 role,保留字段以兼容老前端代码。 */
  roleKey: string;
  /** moduleKey → 该模块所有 action 的 'none|view|full' 字符串。 */
  modules: Record<string, ModulePerms>;
  /**
   * OEM H1(可选,老后端无此字段):角色允许、但租户未开通 / 已停用 / 已过期的计费模块。
   * 不在 {@link modules} 里(侧栏 / ⌘K / 工作台入口不出);只用来让「作品」页与工作台在途 / 最近作品
   * 继续展示这些模块的历史产物(看、下载、删),新建 / 消耗积分的动作一律不可用。
   */
  historyModules?: string[];
  /**
   * OEM 第二批 A7(可选,老后端无此字段):{@link historyModules} 里「已拥有但被服务商 / 平台暂停」的那部分。
   * 只读提示据此区分「已被服务商暂停」与「未开通」。
   */
  pausedModules?: string[];
  /**
   * 1002(可选,老后端无此字段):{@link historyModules} 里「历史上拥有过」(权益行仍在:暂停 / 过期 / 退款)的那部分。
   * 客服舱据此给「已停用 · 仅人工处理」入口;从没拥有过的不出。
   */
  everOwnedModules?: string[];
  /**
   * 1005 向 api 提的可选字段(老后端无):租户订阅状态(active / expired / suspended / refunded / pending)。
   * 「我的套餐」接口只有租户超管读得到,其余角色没有任何口子知道「套餐到期了」—— 有了它全员都能看到到期提示。
   */
  subscriptionStatus?: string | null;
  /** 1005 可选:订阅当前周期结束时刻(ISO,本地时间),到期提示里显示日期用。 */
  subscriptionEndAt?: string | null;
  /**
   * 1011 算法备案(可选,老后端无此字段):当前租户解析后的合规配置(代客时 = 被代租户)。
   * 读一律走 `useCompliance()`(缺省兜底四项全 false),别直接读本字段。
   */
  compliance?: Partial<Compliance> | null;
}

export const permissionService = {
  getSummary() {
    return api.get<PermissionSummary>("/platform/system/permissions/summary");
  },

  getRoles() {
    return api.get<RoleInfo[]>("/platform/system/roles");
  },

  getMatrix() {
    return api.get<PermissionMatrixRow[]>("/platform/system/permissions/matrix");
  },

  /** 拉当前登录用户的有效权限矩阵,登录后 + 页面初次加载都要拉。 */
  getMine() {
    return api.get<MyPermissions>("/me/permissions");
  },
};
